// ClientManagement.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Search, Edit2, User, Mail, Phone, MapPin, ChevronUp, ChevronDown } from "lucide-react";
import { getAuth } from "firebase/auth";
import { toast } from "react-toastify";

import { BASE_URL } from "shared/lib/config";
import { AddClientModal } from "../others/AddClientModal";
import { EditClientModal } from "../others/EditClientModal";

const API_BASE = BASE_URL || "";

/* ===========================
 * Firebase token helper
 * =========================== */
async function getFirebaseIdToken() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken(false);
}

/* ===========================
 * API helper (same pattern as InvoicePenjualan)
 * =========================== */
async function apiFetch(path, options = {}) {
  const token = await getFirebaseIdToken();
  if (!token) throw new Error("No token provided (user not logged in)");

  const hasBody = typeof options.body !== "undefined";
  const isBodyString = typeof options.body === "string";
  const isBodyObject = options.body && typeof options.body === "object" && !Array.isArray(options.body);

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    },
    body: hasBody && !isBodyString && isBodyObject ? JSON.stringify(options.body) : options.body,
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

  if (!res.ok) {
    const message =
      (data && typeof data === "object" && (data.message || data.error)) ||
      (typeof data === "string" && data) ||
      `Request failed (${res.status})`;
    throw new Error(message);
  }

  return data;
}

export default function ClientManagement() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  // optional banner
  const [error, setError] = useState("");

  /* =========================
   * ✅ Pagination + sorting state (client-side)
   * ========================= */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // 10 | 25 | 50 | "all"

  const [sortKey, setSortKey] = useState("client_name"); // client_name | client_id | email | phone | address
  const [sortDir, setSortDir] = useState("asc");

  const fetchClients = async (q = "") => {
    try {
      setError("");
      setLoading(true);

      const qs = q ? `?q=${encodeURIComponent(q)}` : "";
      const data = await apiFetch(`/api/invoice-tracker/clients${qs}`, { method: "GET" });

      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching clients:", err);
      const msg = err?.message || "Gagal memuat data client";
      setError(msg);
      setClients([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createClient = async (payload) => {
    return await apiFetch("/api/invoice-tracker/clients", {
      method: "POST",
      body: payload,
    });
  };

  const updateClient = async (id, payload) => {
    return await apiFetch(`/api/invoice-tracker/clients/${id}`, {
      method: "PUT",
      body: payload,
    });
  };

  const filteredClients = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return clients;

    return (clients || []).filter((client) => {
      const name = String(client?.client_name || "").toLowerCase();
      const cid = String(client?.client_id || "").toLowerCase();
      const email = String(client?.email || "").toLowerCase();
      const phone = String(client?.phone || "").toLowerCase();
      const address = String(client?.address || "").toLowerCase();

      // backward compat if some data still has `contact`
      const contact = String(client?.contact || "").toLowerCase();

      return (
        name.includes(term) ||
        cid.includes(term) ||
        email.includes(term) ||
        phone.includes(term) ||
        address.includes(term) ||
        contact.includes(term)
      );
    });
  }, [clients, searchTerm]);

  const toggleSort = useCallback(
    (key) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    [sortKey]
  );

  const sortedClients = useMemo(() => {
    const arr = Array.isArray(filteredClients) ? [...filteredClients] : [];
    const dir = sortDir === "asc" ? 1 : -1;

    const getVal = (c) => {
      if (sortKey === "client_name") return String(c?.client_name || "");
      if (sortKey === "client_id") return String(c?.client_id || "");
      if (sortKey === "email") return String(c?.email || "");
      if (sortKey === "phone") {
        // prefer phone, fallback contact (legacy)
        return String(c?.phone || c?.contact || "");
      }
      if (sortKey === "address") return String(c?.address || "");
      return "";
    };

    arr.sort((a, b) => getVal(a).localeCompare(getVal(b), "id", { sensitivity: "base" }) * dir);
    return arr;
  }, [filteredClients, sortKey, sortDir]);

  // Reset page whenever list or sort changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, clients, sortKey, sortDir]);

  const total = sortedClients.length;

  const totalPages = useMemo(() => {
    if (pageSize === "all") return 1;
    const ps = Math.max(Number(pageSize) || 10, 1);
    return Math.max(Math.ceil(total / ps), 1);
  }, [total, pageSize]);

  // Keep page in range if total shrinks
  useEffect(() => {
    setPage((p) => Math.min(Math.max(p, 1), totalPages));
  }, [totalPages]);

  const visibleClients = useMemo(() => {
    if (pageSize === "all") return sortedClients;
    const ps = Math.max(Number(pageSize) || 10, 1);
    const start = (Math.max(page, 1) - 1) * ps;
    const end = start + ps;
    return sortedClients.slice(start, end);
  }, [sortedClients, page, pageSize]);

  const fromToText = useMemo(() => {
    if (total === 0) return "Menampilkan 0 data";
    if (pageSize === "all") return `Menampilkan 1-${total} dari ${total} data`;

    const ps = Math.max(Number(pageSize) || 10, 1);
    const start = (page - 1) * ps + 1;
    const end = Math.min(page * ps, total);
    return `Menampilkan ${start}-${end} dari ${total} data`;
  }, [total, page, pageSize]);

  const onSearch = async () => {
    const q = searchTerm.trim();
    const toastId = toast.loading(q ? "Mencari client..." : "Memuat client...");
    try {
      await fetchClients(q); // server-side search (optional)
      toast.dismiss(toastId);
    } catch {
      toast.dismiss(toastId);
    }
  };

  const onReset = async () => {
    setSearchTerm("");
    const toastId = toast.loading("Reset...");
    try {
      await fetchClients("");
      toast.dismiss(toastId);
    } catch {
      toast.dismiss(toastId);
    }
  };

  const SortIcon = ({ active, dir }) => {
    if (!active)
      return (
        <span className="inline-flex opacity-30">
          <ChevronUp className="w-4 h-4" />
        </span>
      );
    return dir === "asc" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Pengelolaan Klien</h2>
          <p className="text-sm text-gray-600 mt-1">Kelola data klien perusahaan</p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Tambah Klien
        </button>
      </div>

      {/* Error banner */}
      {error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      ) : null}

      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Cari client berdasarkan nama, ID, email, nomor, atau alamat..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSearch();
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={onSearch}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Cari
          </button>
          <button
            type="button"
            onClick={onReset}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="text-gray-600 mt-4">Memuat data client...</p>
        </div>
      ) : total === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchTerm ? "Client tidak ditemukan" : "Belum ada client"}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm ? "Coba kata kunci pencarian yang berbeda" : "Mulai tambahkan client pertama Anda"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
           <table className="w-full text-sm table-fixed text-center">

              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-gray-700">
                  <th className="px-6 py-4 text-center font-medium w-[320px]">
                    <button
                      type="button"
                      onClick={() => toggleSort("client_name")}
                      className="inline-flex items-center justify-center gap-2 hover:text-gray-900 w-full"
                      title="Sort by Nama"
                    >
                      Nama <SortIcon active={sortKey === "client_name"} dir={sortDir} />
                    </button>
                  </th>

                  <th className="px-6 py-4 text-center font-medium w-[120px]">
                    <button
                      type="button"
                      onClick={() => toggleSort("client_id")}
                      className="inline-flex items-center justify-center gap-2 hover:text-gray-900 w-full"
                      title="Sort by ID"
                    >
                      ID <SortIcon active={sortKey === "client_id"} dir={sortDir} />
                    </button>
                  </th>

                  <th className="px-6 py-4 text-center font-medium w-[320px]">
                    <button
                      type="button"
                      onClick={() => toggleSort("email")}
                      className="inline-flex items-center justify-center gap-2 hover:text-gray-900 w-full"
                      title="Sort by Email"
                    >
                      Email <SortIcon active={sortKey === "email"} dir={sortDir} />
                    </button>
                  </th>

                  <th className="px-6 py-4 text-center font-medium w-[180px]">
                    <button
                      type="button"
                      onClick={() => toggleSort("phone")}
                      className="inline-flex items-center justify-center gap-2 hover:text-gray-900 w-full"
                      title="Sort by Nomor"
                    >
                      Nomor <SortIcon active={sortKey === "phone"} dir={sortDir} />
                    </button>
                  </th>

                  <th className="px-6 py-4 text-center font-medium w-[420px]">
                    <button
                      type="button"
                      onClick={() => toggleSort("address")}
                      className="inline-flex items-center justify-center gap-2 hover:text-gray-900 w-full"
                      title="Sort by Alamat"
                    >
                      Alamat <SortIcon active={sortKey === "address"} dir={sortDir} />
                    </button>
                  </th>

                  <th className="px-6 py-4 text-center font-medium w-[140px]">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {visibleClients.map((client) => {
                  const id = client?._id || client?.id;

                  const email = String(client?.email || "").trim();
                  const phone = String(client?.phone || "").trim();

                  // backward compat: if old schema still has contact, show it if email/phone empty
                  const legacyContact = String(client?.contact || "").trim();
                  const showLegacyOnly = !phone && !!legacyContact;

                  return (
                    <tr key={id} className="hover:bg-gray-50/50">
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-start gap-3 min-w-0">
                          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 shrink-0 mt-0.5">
                            <User className="w-4 h-4 text-blue-600" />
                          </span>
                          <span className="font-medium text-gray-900 min-w-0 break-words leading-5">
                            {client?.client_name || "-"}
                          </span>
                        </div>
                      </td>

                      <td className="px-6 py-4 align-top">
                        <span className="text-xs text-gray-600">{client?.client_id || "-"}</span>
                      </td>

                      <td className="px-6 py-4 align-top text-center">
                        {email ? (
                          <div className="flex items-center justify-center gap-2 text-gray-700 min-w-0">
                            <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="min-w-0 break-words leading-5">{email}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>

                      <td className="px-6 py-4 align-top text-center">

                        {phone ? (
                         <div className="flex items-center justify-center gap-2 text-gray-700">

                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>{phone}</span>
                          </div>
                        ) : showLegacyOnly ? (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>{legacyContact}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>

                      <td className="px-6 py-4 align-top">
                        {client?.address ? (
                          <div className="flex items-start gap-2 text-gray-700 min-w-0">
                            <MapPin className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                            <span className="min-w-0 break-words line-clamp-2 leading-5">{client.address}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>

                      <td className="px-6 py-4 align-top">
                        <div className="flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => setEditingClient(client)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 text-xs font-medium hover:bg-gray-50 transition-all"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ✅ Pagination footer */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-white">
            <div className="text-xs text-gray-500">{fromToText}</div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span>Tampilkan</span>
                <select
                  value={String(pageSize)}
                  onChange={(e) => {
                    const val = e.target.value === "all" ? "all" : Number(e.target.value);
                    setPageSize(val);
                    setPage(1);
                  }}
                  className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="all">Semua</option>
                </select>
                <span>per halaman</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page <= 1}
                  className={`h-8 w-8 inline-flex items-center justify-center rounded-md border ${
                    page <= 1
                      ? "border-gray-200 text-gray-300 cursor-not-allowed"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                  title="Sebelumnya"
                >
                  ‹
                </button>

                <div className="text-xs text-gray-600">
                  Halaman <span className="font-semibold">{page}</span> dari{" "}
                  <span className="font-semibold">{totalPages}</span>
                </div>

                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  disabled={page >= totalPages}
                  className={`h-8 w-8 inline-flex items-center justify-center rounded-md border ${
                    page >= totalPages
                      ? "border-gray-200 text-gray-300 cursor-not-allowed"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                  title="Berikutnya"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddClientModal
          onClose={() => setShowAddModal(false)}
          onCreate={async (payload) => {
            const toastId = toast.loading("Menyimpan client...");
            try {
              const created = await createClient(payload);
              toast.update(toastId, {
                render: "Client berhasil ditambahkan",
                type: "success",
                isLoading: false,
                autoClose: 2000,
              });
              await fetchClients(searchTerm.trim());
              return created;
            } catch (err) {
              toast.update(toastId, {
                render: err?.message || "Gagal menambahkan client",
                type: "error",
                isLoading: false,
                autoClose: 2500,
              });
              throw err;
            }
          }}
          onSuccess={() => {}}
        />
      )}

      {editingClient && (
        <EditClientModal
          client={editingClient}
          onClose={() => setEditingClient(null)}
          onUpdate={async (id, payload) => {
            const toastId = toast.loading("Menyimpan perubahan...");
            try {
              const updated = await updateClient(id, payload);
              toast.update(toastId, {
                render: "Client berhasil diupdate",
                type: "success",
                isLoading: false,
                autoClose: 2000,
              });
              await fetchClients(searchTerm.trim());
              return updated;
            } catch (err) {
              toast.update(toastId, {
                render: err?.message || "Gagal mengupdate client",
                type: "error",
                isLoading: false,
                autoClose: 2500,
              });
              throw err;
            }
          }}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
}
